import React from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { cn } from '../../lib/utils';

const EXTRA_VARIANTS = {
  emerald: {
    variant: 'default',
    className: 'bg-emerald-600 text-white shadow-sm hover:bg-emerald-700',
  },
  violet: {
    variant: 'outline',
    className: 'border border-violet-300 bg-white text-violet-800 shadow-sm hover:bg-violet-50 hover:text-violet-900',
  },
};

function ShellAction({ action }) {
  const extra = EXTRA_VARIANTS[action.variant];
  const Icon = action.loading ? Loader2 : action.icon;
  const trigger = (
    <Button
      type="button"
      data-action={action.id}
      variant={extra ? extra.variant : action.variant || 'default'}
      size={action.size || 'default'}
      className={extra ? extra.className : undefined}
      onClick={action.onClick}
      disabled={action.disabled || action.loading}
      aria-label={action.size === 'icon' ? action.label : undefined}
      title={action.size === 'icon' ? action.label : undefined}
    >
      {Icon && <Icon className={cn('h-4 w-4', action.loading && 'animate-spin')} />}
      {action.size !== 'icon' && <span>{action.loading ? action.loadingLabel || action.label : action.label}</span>}
    </Button>
  );
  if (action.tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent>{action.tooltip}</TooltipContent>
      </Tooltip>
    );
  }
  return trigger;
}

export default function ModuleShell({
  pageId,
  title,
  subtitle,
  icon,
  actions,
  headerExtra,
  stepper,
  children,
  className,
}) {
  const Icon = icon;
  return (
    <div className={cn('min-h-full space-y-6 bg-slate-50/40 pb-8', className)} data-page-id={pageId}>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          {Icon && (
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-50 text-emerald-700">
              <Icon className="h-5 w-5" />
            </span>
          )}
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">Copiloto de Conversas</p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950">{title}</h1>
            {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
          </div>
        </div>
        {headerExtra && <div className="self-center">{headerExtra}</div>}
        {actions && actions.length > 0 && (
          <TooltipProvider delayDuration={200}>
            <div className="flex items-center gap-2">
              {actions.map((action) => (
                <ShellAction key={action.id} action={action} />
              ))}
            </div>
          </TooltipProvider>
        )}
      </header>
      {stepper && <div>{stepper}</div>}
      {children}
    </div>
  );
}
